'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onWhatsAppClick?: () => void;
}

const CommercialBuildingSVG = () => (
  <svg viewBox="0 0 640 480" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    <defs>
      <linearGradient id="sky" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#0f2744"/>
        <stop offset="100%" stopColor="#1a4a7a"/>
      </linearGradient>
      <linearGradient id="bldg1" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#1a3a5c"/>
        <stop offset="100%" stopColor="#2a5a8c"/>
      </linearGradient>
      <linearGradient id="bldg2" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#0d2a4a"/>
        <stop offset="100%" stopColor="#1e4878"/>
      </linearGradient>
      <linearGradient id="glass" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4a9edd" stopOpacity="0.9"/>
        <stop offset="50%" stopColor="#7ec8f0" stopOpacity="0.6"/>
        <stop offset="100%" stopColor="#b8e4ff" stopOpacity="0.4"/>
      </linearGradient>
      <linearGradient id="glass2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#2a7ab8" stopOpacity="0.8"/>
        <stop offset="100%" stopColor="#5ab0e0" stopOpacity="0.5"/>
      </linearGradient>
      <linearGradient id="ground" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1a2a3a"/>
        <stop offset="100%" stopColor="#0a1520"/>
      </linearGradient>
      <linearGradient id="reflect" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4a9edd" stopOpacity="0.3"/>
        <stop offset="100%" stopColor="#4a9edd" stopOpacity="0"/>
      </linearGradient>
    </defs>

    {/* Sky */}
    <rect width="640" height="480" fill="url(#sky)"/>

    {/* Distant city glow */}
    <ellipse cx="320" cy="340" rx="280" ry="60" fill="#1e5a9a" opacity="0.3"/>

    {/* Far background buildings */}
    <rect x="0" y="260" width="55" height="220" fill="#0d2035" opacity="0.7"/>
    <rect x="560" y="240" width="80" height="240" fill="#0d2035" opacity="0.7"/>
    <rect x="40" y="290" width="40" height="190" fill="#0d2035" opacity="0.5"/>
    <rect x="570" y="270" width="50" height="210" fill="#0d2035" opacity="0.5"/>

    {/* Left tall tower */}
    <rect x="30" y="100" width="130" height="380" fill="url(#bldg2)"/>
    {/* Left tower glass curtain wall */}
    {Array.from({length: 14}).map((_, row) =>
      Array.from({length: 4}).map((_, col) => (
        <rect key={`l-${row}-${col}`}
          x={38 + col * 30} y={108 + row * 26}
          width="24" height="20" rx="1"
          fill="url(#glass2)"
          opacity={0.4 + (col % 2) * 0.2 + (row % 3) * 0.1}
        />
      ))
    )}
    {/* Left tower vertical mullions */}
    {[38, 68, 98, 128, 152].map((x, i) => (
      <line key={`lv-${i}`} x1={x} y1="100" x2={x} y2="480" stroke="#0a2040" strokeWidth="2" opacity="0.6"/>
    ))}

    {/* Centre main tower — tallest */}
    <rect x="195" y="40" width="180" height="440" fill="url(#bldg1)"/>
    {/* Centre tower — full glass facade */}
    {Array.from({length: 20}).map((_, row) =>
      Array.from({length: 5}).map((_, col) => (
        <rect key={`c-${row}-${col}`}
          x={200 + col * 34} y={48 + row * 21}
          width="28" height="16" rx="1"
          fill="url(#glass)"
          opacity={0.35 + (col === 2 ? 0.25 : 0) + (row % 4 === 0 ? 0.15 : 0)}
        />
      ))
    )}
    {/* Centre tower vertical mullions */}
    {[200, 234, 268, 302, 336, 370].map((x, i) => (
      <line key={`cv-${i}`} x1={x} y1="40" x2={x} y2="480" stroke="#0a1e35" strokeWidth="2.5" opacity="0.7"/>
    ))}
    {/* Centre tower horizontal bands every 5 floors */}
    {[40, 145, 250, 355].map((y, i) => (
      <rect key={`ch-${i}`} x="195" y={y} width="180" height="4" fill="#0a1e35" opacity="0.8"/>
    ))}
    {/* Rooftop details */}
    <rect x="255" y="20" width="60" height="24" fill="#1a3a5c"/>
    <rect x="275" y="8" width="8" height="16" fill="#2a5a8c"/>
    <circle cx="279" cy="6" r="3" fill="#e05a20" opacity="0.9"/>
    <rect x="305" y="14" width="6" height="10" fill="#2a5a8c"/>

    {/* Right tower — medium */}
    <rect x="400" y="140" width="155" height="340" fill="url(#bldg2)"/>
    {Array.from({length: 13}).map((_, row) =>
      Array.from({length: 4}).map((_, col) => (
        <rect key={`r-${row}-${col}`}
          x={408 + col * 35} y={150 + row * 24}
          width="28" height="18" rx="1"
          fill="url(#glass)"
          opacity={0.3 + (col % 2) * 0.2 + (row % 3) * 0.1}
        />
      ))
    )}
    {[408, 443, 478, 513, 548].map((x, i) => (
      <line key={`rv-${i}`} x1={x} y1="140" x2={x} y2="480" stroke="#0a2040" strokeWidth="2" opacity="0.6"/>
    ))}

    {/* Far right shorter building */}
    <rect x="545" y="210" width="95" height="270" fill="#0d2035" opacity="0.85"/>
    {Array.from({length: 9}).map((_, row) =>
      Array.from({length: 2}).map((_, col) => (
        <rect key={`fr-${row}-${col}`}
          x={553 + col * 40} y={218 + row * 26}
          width="32" height="20" rx="1"
          fill="url(#glass2)"
          opacity={0.25 + (row % 2) * 0.15}
        />
      ))
    )}

    {/* Ground / plaza */}
    <rect x="0" y="440" width="640" height="40" fill="url(#ground)"/>
    {/* Ground reflection */}
    <rect x="0" y="440" width="640" height="40" fill="url(#reflect)"/>
    {/* Plaza lines */}
    <line x1="0" y1="442" x2="640" y2="442" stroke="#2a4a6a" strokeWidth="1" opacity="0.5"/>
    <line x1="0" y1="448" x2="640" y2="448" stroke="#2a4a6a" strokeWidth="0.5" opacity="0.3"/>
    {/* Street lights */}
    <line x1="170" y1="390" x2="170" y2="440" stroke="#3a5a7a" strokeWidth="2"/>
    <ellipse cx="170" cy="388" rx="8" ry="3" fill="#ffe080" opacity="0.7"/>
    <ellipse cx="170" cy="388" rx="16" ry="6" fill="#ffe080" opacity="0.15"/>
    <line x1="470" y1="390" x2="470" y2="440" stroke="#3a5a7a" strokeWidth="2"/>
    <ellipse cx="470" cy="388" rx="8" ry="3" fill="#ffe080" opacity="0.7"/>
    <ellipse cx="470" cy="388" rx="16" ry="6" fill="#ffe080" opacity="0.15"/>

    {/* Moon */}
    <circle cx="580" cy="55" r="22" fill="#d4e8f8" opacity="0.9"/>
    <circle cx="590" cy="48" r="18" fill="#1a3a5c"/>

    {/* Stars */}
    {[[60,30],[120,55],[500,25],[540,60],[90,80],[440,40],[160,20]].map(([x,y], i) => (
      <circle key={`s-${i}`} cx={x} cy={y} r="1.2" fill="white" opacity={0.6 + (i % 3) * 0.2}/>
    ))}

    {/* Lens flare on centre tower */}
    <ellipse cx="285" cy="90" rx="3" ry="8" fill="white" opacity="0.15" transform="rotate(-20 285 90)"/>
  </svg>
);

export default function HeroSection({ onWhatsAppClick }: HeroSectionProps) {
  const trustBadges = [
    { icon: 'ClockIcon', title: '24/7 On-Call Availability', subtitle: 'Quick Response' },
    { icon: 'ShieldCheckIcon', title: 'Fully Insured', subtitle: 'Licensed & Certified' },
    { icon: 'MapPinIcon', title: 'Lower Mainland', subtitle: 'Local Experts' },
  ];

  return (
    <section className="relative bg-gradient-to-br from-dark-bg via-darker-bg to-dark-bg overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary-light backdrop-blur-sm">
              <Icon name="StarIcon" size={16} variant="solid" />
              <span>Trusted Across the Lower Mainland</span>
            </div>

            <div className="space-y-4">
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Your Full-Service
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-accent mt-2">
                  Property Maintenance
                </span>
                Partner
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
                Professional commercial property services across the Lower Mainland. From building maintenance to specialized staffing, we&apos;re your single-source partner.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                href="/contact"
                className="group inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary text-primary-foreground rounded-xl hover:bg-primary-dark transition-all duration-300 text-base font-semibold shadow-lg hover:shadow-xl hover:scale-105"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                <span>Chat with us live now</span>
                <Icon name="ArrowRightIcon" size={18} variant="outline" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              {onWhatsAppClick && (
                <button
                  onClick={onWhatsAppClick}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-success text-success-foreground rounded-xl hover:bg-success/90 transition-all duration-300 text-base font-semibold shadow-lg hover:shadow-xl"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                  <span>WhatsApp Us</span>
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex flex-col items-center lg:items-start gap-2 text-center lg:text-left">
                  <div className="p-2.5 bg-primary/10 rounded-lg backdrop-blur-sm">
                    <Icon name={badge.icon as any} size={20} variant="outline" className="text-primary-light" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{badge.title}</p>
                    <p className="text-xs text-slate-400">{badge.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Commercial Building Illustration */}
          <div className="relative lg:block hidden">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <CommercialBuildingSVG />
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
}
